/**
 * One tracking URL, in full: what it is, what it earned, and where from.
 *
 * A sheet rather than a route, which is how every other detail view in this
 * workspace works — a client, a deal, a campaign all open this way — so the
 * list stays behind it and closing returns you to your place in it.
 */
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { useTracking, changed } from '../../lib/urltracking.js';
import { formatDate, parseDate } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import Sheet, { Group } from '../Sheet.jsx';
import { ClicksChart, Breakdown, TrackStatus, CopyUrl, DwellTiles, num, duration, placeOf, ipOf, clickTime, clickStamp, Told } from '../../pages/urltracking/TrackBits.jsx';
import QrCode from '../../pages/urltracking/QrCode.jsx';

export default function TrackLinkView({ id, onClose }) {
  const { S, setSheet, can, toast } = useApp();
  const { data, error, loading } = useTracking(() => api.urlTracking.analytics.link(id), [id]);

  const link = data?.link;
  const t = data?.totals || {};

  const flip = async () => {
    try {
      await (link.status === 'Active'
        ? api.urlTracking.links.pause(id)
        : api.urlTracking.links.activate(id));
      toast(link.status === 'Active' ? 'Tracking URL paused' : 'Tracking URL active');
      changed();
    } catch (e) {
      toast(e.message);
    }
  };

  return (
    <Sheet
      icon="link"
      tone="g-cyan"
      title={link?.name || 'Tracking URL'}
      badge={link ? <TrackStatus value={link.status} /> : null}
      onClose={onClose}
      closeLabel="Close"
      wide
      footerLeft={link && can('utm-links', 'edit') ? (
        <>
          <button type="button" className="btn" onClick={flip}>
            <Icon name={link.status === 'Active' ? 'wait' : 'check'} />
            {link.status === 'Active' ? 'Pause' : 'Activate'}
          </button>
          <button type="button" className="btn" onClick={() => setSheet({ kind: 'trackPersonalise', id })}>
            <Icon name="users" />Per recipient
          </button>
          <button type="button" className="btn" onClick={() => setSheet({ kind: 'trackLink', id })}>
            <Icon name="edit" />Edit
          </button>
        </>
      ) : null}
    >
      {error ? <p className="utbad">{error}</p> : null}
      {loading && !data ? <p className="muted">Loading…</p> : null}

      {link ? (
        <>
          <div className="f wide">
            <div className="utmadeurl flat">
              <code>{link.short_url}</code>
              <CopyUrl url={link.short_url} />
            </div>
          </div>

          <div className="utstats">
            {[
              ['Total clicks', num(t.clicks)],
              ['Unique visitors (est.)', num(t.uniqueVisitors)],
              ['Today', num(t.clicksToday)],
              ['This week', num(t.clicksThisWeek)],
              ['This month', num(t.clicksThisMonth)]
            ].map(([label, value]) => (
              <div key={label}><b>{value}</b><span>{label}</span></div>
            ))}
          </div>

          <Group>Time on the destination</Group>
          <div className="f wide"><DwellTiles dwell={data.dwell} /></div>

          <Group>QR code</Group>
          <div className="f wide">
            <QrCode url={link.short_url} name={link.name || link.short_code} />
          </div>

          <Group>Clicks over time</Group>
          <div className="f wide"><ClicksChart series={data.series} height={160} /></div>

          <Group>Where the clicks came from</Group>
          <div className="f wide utbreak">
            <Breakdown title="Device" icon="grid" tone="g-cyan" rows={data.devices} />
            <Breakdown title="Browser" icon="eye" tone="g-purple" rows={data.browsers} />
            <Breakdown
              title="Country"
              icon="building"
              tone="g-green"
              rows={data.countries}
              note="From an edge header, or a lookup service when one is configured."
            />
          </div>

          <Group>Details</Group>
          <div className="f wide">
            <dl className="utdl">
              <dt>Campaign</dt><dd>{link.campaign_name}</dd>
              {link.client_name ? <><dt>Recipient</dt><dd>{link.client_name}</dd></> : null}
              <dt>Destination</dt><dd className="brk">{link.destination_url}</dd>
              <dt>Visitors land on</dt><dd className="brk">{link.target_url}</dd>
              <dt>UTM source</dt><dd>{link.utm_source || '—'}</dd>
              <dt>UTM medium</dt><dd>{link.utm_medium || '—'}</dd>
              <dt>UTM campaign</dt><dd>{link.utm_campaign || '—'}</dd>
              <dt>UTM term</dt><dd>{link.utm_term || '—'}</dd>
              <dt>UTM content</dt><dd>{link.utm_content || '—'}</dd>
              <dt>Created by</dt><dd>{link.created_by_name || '—'}</dd>
              <dt>Created</dt><dd>{clickStamp(link.created_at, S.tz)}</dd>
              <dt>First click</dt><dd>{t.firstClick ? clickStamp(t.firstClick, S.tz) : 'Never clicked'}</dd>
              <dt>Last click</dt><dd>{t.lastClick ? clickStamp(t.lastClick, S.tz) : '—'}</dd>
            </dl>
          </div>

          {data.recent?.length ? (
            <>
              <Group>Recent clicks</Group>
              <div className="f wide tscroll">
                <table>
                  <thead>
                    <tr><th>Time</th><th>Location</th><th>IP</th><th>Device</th><th>Browser</th><th>Time on site</th></tr>
                  </thead>
                  <tbody>
                    {data.recent.map((c, i) => (
                      <tr key={`${c.at}-${i}`}>
                        <td>{clickTime(c.at, S.tz)}</td>
                        <td><Told of={placeOf(c)} className="dim" /></td>
                        <td><Told of={ipOf(c)} className="utip" /></td>
                        <td className="dim">{c.device}</td>
                        <td className="dim">{c.browser}</td>
                        <td>
                          {duration(c.dwellSeconds) || (
                            <span className="dim" title="Only destinations carrying the beacon script can report how long a visit lasted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </Sheet>
  );
}

